// import { ArrowDown } from "@carbon/react/icons"; // typescript error
import { Button } from "@carbon/react";
import { Add, ArrowDown, TrashCan } from "@carbon/icons-react"; // types available

import {
  DatePicker,
  ModalContainer,
  SectionHeader,
  SwitchAppButton,
  SwitchAppContentSwitcher,
  SwitchAppIconButton,
} from "../../../components";
import { IAccountStatementViewProps, TStatementKind } from "./AccountStatement";
import {
  TextInput as HookFormInput,
  HookFormToggle,
  SubmitButton,
} from "../../../hook-form";
import { statementTargets } from "../../../enums";
import moment from "moment";
import { appConfig } from "../../../configs";
import { Routes, Route } from "react-router-dom";

export const View = (props: IAccountStatementViewProps) => {
  const { register, errors } = props;

  const todayMaxDate = moment().format(appConfig.javascriptCodeDateFormat);
  const switchAppOpsMinDate = moment("Dec 1, 2022").format(
    appConfig.javascriptCodeDateFormat
  );

  const targetActionDescription =
    props.formValues.target === "download"
      ? `Download ${props.formValues.statementDocumentType.toUpperCase()} Statement`
      : `Send ${props.formValues.statementDocumentType.toUpperCase()} Email(s)`;

  return (
    <>
      <div className="flex-container row align-items--end">
        <Button
          onClick={props.navigateToNewTopUp}
          size="sm"
          kind="tertiary"
          className="switchapp-themed create-new-button"
          iconDescription="Add to balance"
        >
          <Add size={20} />
          Top-Up Your Wallet
        </Button>

        <SwitchAppButton
          onClick={props.openStatementsModal}
          size="sm"
          kind="secondary"
          className="switchapp-themed create-new-button"
          title="Download/Mail Account Statements"
        >
          ACCOUNT STATEMENTS
          <ArrowDown size={20} />
        </SwitchAppButton>
      </div>

      <Routes>
        <Route
          path="statements"
          element={
            <form
              className="download-statement--form"
              onSubmit={props.handleSubmit}
            >
              <ModalContainer
                isModalOpen={true}
                closeModal={props.closeStatementsModal}
                // fullscreen
                modalHeader={
                  <SectionHeader
                    title="SWITCHAPP STATMENTS"
                    subtitle="Download/email your statements in pdf or csv formats."
                    renderRight={
                      <div className="flex-container justify-content--end mt-5">
                        <div className="flex-container">
                          <SwitchAppButton
                            kind="secondary"
                            onClick={props.closeStatementsModal}
                            size="sm"
                            className="d-none d-md-inherit"
                          >
                            Cancel
                          </SwitchAppButton>

                          <SubmitButton
                            kind="primary"
                            size="sm"
                            className="ml-5"
                            // disabled={false}
                            isLoading={props.isLoadingStatement}
                            showChildrenWithLoadingIcon
                            title={`${targetActionDescription} of statement`}
                          >
                            {targetActionDescription}
                          </SubmitButton>
                        </div>
                      </div>
                    }
                  />
                }
              >
                <div className="flex-container justify-content--center flex-break--small mb-5">
                  <div className="flex-box alignment-helper" />
                  <div className="flex-box flex-2">
                    <SwitchAppContentSwitcher<TStatementKind>
                      onChange={({ name }) => {
                        props.setStatementKind(name);

                        // TODO: remove this block after implementing PDF for payments statement
                        if (props.formValues.statementKind === "wallet") {
                          props.setStatementDocumentType("csv");
                        } else {
                          props.setStatementDocumentType("pdf"); // TODO: remove this line after implementing PDF for payments statement
                          props.setStatementTarget(statementTargets.email); // TODO: remove this line after fixing blank PDF bug
                        }
                      }}
                      options={[
                        {
                          key: "Account Statement (e-signed)",
                          value: "wallet",
                        },
                        {
                          key: "Payments Statement",
                          value: "payments",
                        },
                      ]}
                      selectedValue={props.formValues.statementKind}
                    />

                    <div className="flex-container col flex-break--small mt-5">
                      <div className="flex-container align-items--center">
                        <strong className="text-secondary mr-5">From </strong>
                        <DatePicker
                          name="statementStartPeriod"
                          label="Start Period"
                          placeholder={
                            props.formValues.start_date ?? "Start Period"
                          }
                          defaultValue={props.formValues.start_date ?? null}
                          onChange={(date) =>
                            props.setStatementStartPeriod(
                              moment(new Date(date)).format(
                                appConfig.javascriptCodeDateFormat
                              )
                            )
                          }
                          maxDate={todayMaxDate}
                          minDate={switchAppOpsMinDate}
                        />
                      </div>

                      <div className="flex-container align-items--center">
                        <strong className="text-secondary mr-5">To </strong>

                        <div className="ml-5">
                          <DatePicker
                            name="statementEndPeriod"
                            label="End Period"
                            placeholder={
                              props.formValues.end_date ?? "End Period"
                            }
                            defaultValue={props.formValues.end_date ?? null}
                            onChange={(date) =>
                              props.setStatementEndPeriod(
                                moment(new Date(date)).format(
                                  appConfig.javascriptCodeDateFormat
                                )
                              )
                            }
                            maxDate={todayMaxDate}
                            minDate={switchAppOpsMinDate}
                          />
                        </div>
                      </div>

                      <div className="flex-container mt-5">
                        <HookFormToggle
                          name="toggle_download_or_email"
                          label="Send As Email"
                          labelOff="No"
                          labelOn="Yes"
                          size="sm"
                          containerClassName="settings-toggle"
                          defaultToggled={props.formValues.target === "email"}
                          onToggle={() =>
                            props.setStatementTarget(
                              props.formValues.target ===
                                statementTargets.download
                                ? statementTargets.email
                                : statementTargets.download
                            )
                          }
                          infoIconText={
                            <>
                              Select email if you wish to send an e-signed
                              statement to the specified emails
                            </>
                          }
                          disabled={
                            // NOTE: remove after fixing wallet PDF download bug causing blank docs
                            props.formValues.statementKind === "wallet"
                          }
                          register={register}
                          errors={errors}
                        />
                      </div>

                      <div className="">
                        <HookFormToggle
                          name="toggle_csv_or_pdf"
                          label="Type (PDF/CSV)"
                          // labelOff="Csv"
                          // labelOn="PDF"
                          labelOff={props.formValues.statementDocumentType.toUpperCase()}
                          labelOn={props.formValues.statementDocumentType.toUpperCase()}
                          size="sm"
                          containerClassName="settings-toggle"
                          defaultToggled={
                            props.formValues.statementDocumentType === "pdf"
                          }
                          onToggle={() =>
                            props.setStatementDocumentType(
                              props.formValues.statementDocumentType === "csv"
                                ? "pdf"
                                : "csv"
                            )
                          }
                          infoIconText={
                            <>
                              Select whether you want a PDF document or Csv
                              {props.formValues.statementKind === "payments" ? (
                                <>
                                  <br />
                                  <hr />
                                  <span className="text-warning">
                                    NOTE: Payments statement are only available
                                    in CSV format for now.
                                  </span>
                                </>
                              ) : null}
                            </>
                          }
                          disabled={
                            props.formValues.statementKind === "payments"
                          }
                          register={register}
                          errors={errors}
                        />
                      </div>

                      {props.formValues.statementKind === "wallet" ? null : (
                        <div className="flex-container mb-5">
                          <HookFormToggle
                            name="toggle_add_payouts_to_statement"
                            label="Include Payouts"
                            labelOff="No"
                            labelOn="Yes"
                            size="sm"
                            containerClassName="settings-toggle"
                            defaultToggled={props.formValues.display_payouts}
                            onToggle={() =>
                              props.setIncludePayouts(
                                !props.formValues.display_payouts
                              )
                            }
                            infoIconText={
                              <>
                                Select this if you wish to include the
                                payout/settlement breakdown for each payment
                              </>
                            }
                            register={register}
                            errors={errors}
                          />
                        </div>
                      )}

                      {props.formValues.target !== "email" ? null : (
                        <div className="mt-5">
                          {/* <FormGroup
                            style={{
                              maxWidth: "400px",
                            }}
                            legendText="Email Recipients"
                          > */}
                          <div className="cds--structured-list">
                            <div className="cds--structured-list-thead">
                              <div className="cds--structured-list-row cds--structured-list-row--header-row">
                                <div className="cds--structured-list-th font-ctm--semibold"></div>
                                <div className="cds--structured-list-th font-ctm--semibold">
                                  <strong
                                    className={`text-${
                                      errors["emails"] ? "danger" : "black"
                                    }`}
                                  >
                                    {errors["emails"] ? (
                                      <p>{errors["emails"].message}</p>
                                    ) : (
                                      "Email Recipients"
                                    )}
                                  </strong>
                                </div>
                                <div className="cds--structured-list-th font-ctm--semibold">
                                  Actions
                                </div>
                              </div>
                            </div>

                            <div className="cds--structured-list-tbody">
                              {/* <Stack gap={7}> */}
                              {props.formValues.emails.map((email, index) => {
                                return (
                                  <div
                                    // style={virtualizedListStyle}
                                    className={`cds--structured-list-row`}
                                    title={`Recipient ${index + 1} of ${
                                      props.formValues.emails.length
                                    }`}
                                    key={`${email}.${index}`}
                                  >
                                    <div className="cds--structured-list-td font-ctm--semibold text-primary">
                                      {index + 1}.
                                    </div>

                                    <div className="cds--structured-list-td">
                                      <HookFormInput
                                        name={`emails.${index}.email`}
                                        label=""
                                        autoComplete="email"
                                        className=""
                                        type="text"
                                        inputPattern="[a-z0-9._%±]+@ [a-z0-9.-]+\. [a-z] {2,4}$"
                                        register={register}
                                        errors={errors}
                                      />
                                      <small
                                        className={`text-${
                                          errors["emails"] ? "danger" : "black"
                                        }`}
                                      >
                                        {errors["emails"]?.[index] ? (
                                          <>
                                            {
                                              errors["emails"]?.[index]?.email
                                                ?.message
                                            }
                                          </>
                                        ) : null}
                                      </small>
                                    </div>

                                    <div className="cds--structured-list-td">
                                      {props.formValues.emails.length <
                                      2 ? null : (
                                        <div>
                                          <SwitchAppIconButton
                                            size="sm"
                                            kind="tertiary"
                                            onClick={() =>
                                              props.removeEmailField(index)
                                            }
                                            className="switch-edit-mode--button"
                                            title={"Remove email"}
                                          >
                                            <TrashCan
                                              className="text-danger b-r-5 ml-5"
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                              }}
                                            />
                                          </SwitchAppIconButton>
                                        </div>
                                      )}
                                    </div>

                                    <div className="cds--structured-list-td">
                                      {index !==
                                      props.formValues.emails.length -
                                        1 ? null : (
                                        <div>
                                          <SwitchAppIconButton
                                            size="sm"
                                            kind="tertiary"
                                            onClick={() =>
                                              props.appendEmailField()
                                            }
                                            className="switch-edit-mode--button"
                                            title={"Add new email"}
                                          >
                                            <Add
                                              className="text-primary b-r-5 ml-5"
                                              style={{
                                                width: "30px",
                                                height: "30px",
                                                border: "1px solid",
                                              }}
                                            />
                                          </SwitchAppIconButton>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {/* </Stack> */}
                            </div>
                          </div>
                          {/* </FormGroup> */}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-box alignment-helper" />
                </div>
              </ModalContainer>
            </form>
          }
        />
      </Routes>
    </>
  );
};
